import React, { useState, useCallback, useEffect, useRef } from 'react';  
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRobot,
  faQuestionCircle,
  faChartLine,
  faComments,
  faTimes,
  faPlay,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { aiCopilotService } from '../../services/aiCopilotService';
import { useAICopilot } from '../../hooks/useAICopilot';
import { useErrorHandler } from '../../context/ErrorContext';
import { aiLogger, aiCorrelationMiddleware } from '../../utils/aiLogger';
import styles from './TraderDashboard.module.css';

const CompactAICopilot = ({ 
  strategyParams, 
  marketData, 
  analysis,
  analyzeStrategyEnhanced,
  askQuestion,
  onClose
}) => {
  // Generate correlation ID for this component instance
  const correlationIdRef = useRef(aiCorrelationMiddleware().cid);
  const cid = correlationIdRef.current;
  
  // Use error handler hook
  const { handleError, clearComponentErrors } = useErrorHandler('CompactAICopilot');
  
  // Use the hook as a fallback if props are not provided
  const copilotHook = useAICopilot();
  
  // Create stable references to the functions we need
  const getAnalyzeStrategyEnhanced = useCallback(() => {
    return analyzeStrategyEnhanced || copilotHook.analyzeStrategyEnhanced;
  }, [analyzeStrategyEnhanced, copilotHook.analyzeStrategyEnhanced]);
  
  const [mode, setMode] = useState('strategy');
  const [strategyType, setStrategyType] = useState('momentum');
  const [userInput, setUserInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [localError, setLocalError] = useState(null); // For inline errors
  const [aiResponse, setAiResponse] = useState(null);
  
  const isRequestInProgress = useRef(false);
  
  // Log component mount
  useEffect(() => {
    aiLogger.componentMount(cid, 'CompactAICopilot', { mode: 'initial' });
    
    // Clear component errors when unmounting
    return () => {
      aiLogger.componentUpdate(cid, 'CompactAICopilot', 'unmount');
      clearComponentErrors();
    };
  }, [cid, clearComponentErrors]);
  
  // Strategy options
  const strategyOptions = [
    { value: 'momentum', label: 'Momentum' },
    { value: 'mean_reversion', label: 'Mean Reversion' },
    { value: 'breakout', label: 'Breakout' },
    { value: 'trend_following', label: 'Trend Following' },
    { value: 'swing', label: 'Swing Trading' }
  ];

  const handleAnalyze = useCallback(async () => {
    const requestId = `ai-req-${Date.now()}`;
    
    if (isAnalyzing || isRequestInProgress.current) {
      aiLogger.requestBlocked(cid, 'request_in_progress', { requestId });
      return;
    }

    if (mode === 'qa' && (!userInput || userInput.trim().length === 0)) {
      setLocalError('Please enter a question');
      return;
    }

    // Clear previous errors
    setLocalError(null);
    clearComponentErrors();
    
    // Log request start
    aiLogger.requestStart(cid, mode === 'strategy' ? 'analyze-strategy' : 'simpleQA', {
      requestId,
      mode,
      strategyType: mode === 'strategy' ? strategyType : null,
      inputLength: userInput.length
    });
    
    setIsAnalyzing(true);
    isRequestInProgress.current = true;

    try {
      if (mode === 'strategy') {
        const marketContext = marketData ? {
          tickers: marketData.tickers || [],
          date_range: `${marketData.startDate} to ${marketData.endDate}`,
          market_segment: marketData.marketSegment || 'custom'
        } : {};

        const analyzeFn = getAnalyzeStrategyEnhanced();
        
        aiLogger.userAction(cid, 'CompactAICopilot', 'strategy_analysis', {
          strategyType,
          hasMarketData: !!marketData,
          userQueryLength: userInput.trim().length
        });

        const result = await analyzeFn({
          strategyType,
          strategyParams,
          marketContext,
          userQuery: userInput.trim() || null,
          correlationId: cid
        });
        
        aiLogger.requestSuccess(cid, 'analyze-strategy', result);
        
      } else {
        // Q&A Mode - Use simpleQA endpoint
        aiLogger.userAction(cid, 'CompactAICopilot', 'qa_request', {
          questionLength: userInput.trim().length
        });

        const result = await aiCopilotService.simpleQA({
          question: userInput.trim(),
          systemPrompt: "You are a helpful trading assistant. Answer trading and finance questions clearly and concisely.",
          correlationId: cid
        });
        
        if (result && result.success !== false) {
          // Format the response
          const formattedResponse = aiCopilotService.formatSimpleQAResponse 
            ? aiCopilotService.formatSimpleQAResponse(result)
            : {
                success: true,
                answer: result.analysis || result.answer || result.response || 'AI responded successfully',
                timestamp: result.timestamp || new Date().toISOString()
              };
          
          setAiResponse({
            success: true,
            answer: formattedResponse.answer,
            timestamp: formattedResponse.timestamp,
            mode: 'qa'
          });
          
          aiLogger.requestSuccess(cid, 'simpleQA', result);
        } else {
          throw new Error(result?.error || 'Q&A failed');
        }
      }
      
    } catch (error) {
      // Log the error
      aiLogger.requestError(cid, mode === 'strategy' ? 'analyze-strategy' : 'simpleQA', error, {
        requestId,
        userInputPreview: userInput.substring(0, 50)
      });
      
      // Add to global error system
      handleError(error, {
        requestId,
        mode,
        strategyType: mode === 'strategy' ? strategyType : null,
        cid
      });
      
      // Also show inline error for user feedback
      if (error.message !== 'Component unmounted') {
        // Create user-friendly error message
        let userMessage = 'AI analysis failed';
        
        if (error.response?.status === 429) {
          userMessage = 'Rate limit reached. Please try again in a few minutes.';
        } else if (error.response?.status === 500) {
          userMessage = 'AI service temporarily unavailable.';
        } else if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
          userMessage = 'Network connection issue.';
        } else if (error.response?.data?.detail) {
          userMessage = error.response.data.detail;
        } else if (error.message) {
          userMessage = error.message;
        }
        
        setLocalError(userMessage);
      }
    } finally {
      setIsAnalyzing(false);
      isRequestInProgress.current = false;
    }
  }, [
    mode, 
    strategyType, 
    strategyParams, 
    marketData, 
    userInput, 
    isAnalyzing,
    getAnalyzeStrategyEnhanced,
    cid,
    handleError,
    clearComponentErrors
  ]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      aiLogger.userAction(cid, 'CompactAICopilot', 'enter_key_submit');
      handleAnalyze();
    }
  };

  const handleModeChange = useCallback((newMode) => {
    aiLogger.userAction(cid, 'CompactAICopilot', 'mode_change', {
      from: mode,
      to: newMode
    });
    
    setMode(newMode);
    setLocalError(null);
    setAiResponse(null);
    clearComponentErrors(); // Clear errors when changing modes
  }, [mode, cid, clearComponentErrors]);

  const clearResponse = () => {
    aiLogger.userAction(cid, 'CompactAICopilot', 'clear_response');
    setAiResponse(null);
    setUserInput('');
  };

  // Render local AI response (for Q&A mode)
  const renderAIResponse = () => {
    if (!aiResponse?.success) return null;
    
    return (
      <div className={styles.aiResponseContainer}>
        <div className={styles.aiResponseHeader}>
          <FontAwesomeIcon icon={faRobot} />
          <span> AI Answer:</span>
          <button 
            onClick={clearResponse}
            className={styles.clearResponseButton}
            title="Clear response"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className={styles.aiResponseContent}>
          {aiResponse.answer}
        </div>
        {aiResponse.timestamp && (
          <div className={styles.aiResponseTimestamp}>
            {new Date(aiResponse.timestamp).toLocaleTimeString()}
          </div>
        )}
      </div>
    );
  };

  // Render analysis from parent prop (for Strategy mode)
  const renderParentAnalysis = () => {
    if (!analysis?.success) return null;
    
    return analysis.answer ? (
      <div className={styles.aiResponse}>
        <div className={styles.aiResponseHeader}>
          <FontAwesomeIcon icon={faRobot} />
          <span> AI Strategy Analysis:</span>
        </div>
        <p className={styles.aiResponseText}>{analysis.answer}</p>
      </div>
    ) : null;
  };

  return (
    <div className={styles.compactAICopilotCompact}>
      <div className={styles.aiControlRowCompact}>
        {/* Mode buttons - modern pills */}
        <div className={styles.modePills}>
          <button 
            type="button"
            onClick={() => handleModeChange('strategy')}
            className={`${styles.modePill} ${mode === 'strategy' ? styles.activePill : ''}`}
            disabled={isAnalyzing}
            title="Strategy Analysis Mode"
          >
            <FontAwesomeIcon icon={faChartLine} size="sm" />
            <span>Strategy</span>
          </button>
          
          <button 
            type="button"
            onClick={() => handleModeChange('qa')}
            className={`${styles.modePill} ${mode === 'qa' ? styles.activePill : ''}`}
            disabled={isAnalyzing}
            title="Q&A Mode"
          >
            <FontAwesomeIcon icon={faComments} size="sm" />
            <span>Q&A</span>
          </button>
        </div>
        
        {/* Strategy dropdown - only in strategy mode */}
        {mode === 'strategy' && (
          <select 
            value={strategyType}
            onChange={(e) => setStrategyType(e.target.value)}
            className={styles.strategySelectPill}
            disabled={isAnalyzing}
          >
            {strategyOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      </div>
      
      <div className={styles.aiInputAreaCompact}>
        <textarea 
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isAnalyzing}
          className={styles.aiTextInputCompact}
          placeholder={
            mode === 'strategy' 
              ? `Ask about ${strategyType} strategy...`
              : 'Ask me anything about trading...'
          }
          rows={2}
        />
        
        {/* Play button - circular */}
        <button 
          type="button"
          onClick={handleAnalyze} 
          disabled={isAnalyzing || (mode === 'qa' && !userInput.trim())}
          className={styles.actionButtonPill}
          title={mode === 'strategy' ? `Analyze ${strategyType}` : 'Ask Question'}
        >
          {isAnalyzing ? (
            <div className={styles.spinnerMini}></div>
          ) : (
            <FontAwesomeIcon icon={faPlay} size="sm" />
          )}
        </button>
      </div>
      
      {/* Error display */}
      {localError && (
        <div className={styles.errorMessage}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{localError}</span>
        </div>
      )}
      
      {/* Show appropriate response based on mode */}
      {mode === 'qa' ? renderAIResponse() : renderParentAnalysis()}
    </div>
  );
};

CompactAICopilot.propTypes = {
  strategyParams: PropTypes.object,
  marketData: PropTypes.shape({
    tickers: PropTypes.array,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    marketSegment: PropTypes.string
  }),
  analysis: PropTypes.shape({
    success: PropTypes.bool,
    answer: PropTypes.string
  }),
  analyzeStrategyEnhanced: PropTypes.func,
  askQuestion: PropTypes.func,
  onClose: PropTypes.func.isRequired
};

export default CompactAICopilot;